import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { HOME_CONTENT_MOCK } from '../data/home-content.mock';
import type {
  HomeFaqBannersActivosResponse,
  HomeFaqBannerRow,
  HomeFaqContent,
  HomeFaqItem,
} from '../models/home-faq-content.model';
import { mapFaqBannersToContent } from '../utils/home-faq-banner.mapper';
import { prepareFaqTitleHtml } from '../utils/home-faq-html.util';

/** Debe coincidir con `NeoBannerDTO.FAQ_HOME`. */
export const FAQ_BANNER_TIPO = 'FaqHome';

@Injectable({
  providedIn: 'root',
})
export class HomeFaqService {
  private readonly fallbackContent: HomeFaqContent = buildFaqFallbackFromMock();

  constructor(private readonly bancoldexService: BancoldexService) {}

  loadContent(): Observable<HomeFaqContent> {
    return this.bancoldexService.getArchivosBannerActivos(FAQ_BANNER_TIPO).pipe(
      map((response: HomeFaqBannersActivosResponse) => {
        const rows = normalizeFaqBannerRows(response);
        if (!rows.length) {
          return this.fallbackContent;
        }
        return mapFaqBannersToContent(rows, this.fallbackContent);
      }),
      catchError(() => of(this.fallbackContent))
    );
  }
}

function buildFaqFallbackFromMock(): HomeFaqContent {
  const mock = HOME_CONTENT_MOCK.faq;
  return {
    eyebrow: mock.eyebrow,
    titleHtml: prepareFaqTitleHtml(mock.title),
    items: mock.items.map(
      (item, index): HomeFaqItem => ({
        orden: index + 1,
        pregunta: item.pregunta,
        respuesta: item.respuesta,
      })
    ),
  };
}

function normalizeFaqBannerRows(
  response: HomeFaqBannersActivosResponse
): HomeFaqBannerRow[] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }

  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as HomeFaqBannerRow[][]).flat();
  }

  return response.banners as HomeFaqBannerRow[];
}
